// Content for the 5 core service pages.
// Same shape as CITIES — each page gets unique copy, feature list, materials,
// process, FAQs and a CTA banner.

export const SERVICES = {
  "media-walls": {
    slug: "media-walls",
    title: "Custom Media Walls",
    seo_title: "Custom Media Walls Tampa Bay | Swift Luxury Walls",
    meta_description:
      "Custom media walls in Tampa Bay — floating TVs, integrated fireplaces, marble panels, wood slats and hidden storage designed and installed by Swift Luxury Walls.",
    overline: "Signature service",
    hero_heading: "Custom media walls that anchor the whole room",
    hero_sub:
      "The main event of any great room. We design and install full media walls — TV, fireplace, storage and lighting — as one seamless build engineered around your space.",
    hero_image: "condo-marble-slat",
    features: [
      "Recessed or surface-mount TV integration up to 98\"",
      "Linear electric fireplaces built directly into the wall",
      "Marble-look, PU stone, acrylic, painted or wood finishes",
      "Floating consoles or floor-standing cabinetry",
      "Wood-slat wings and acoustic panel accents",
      "Backlit crown or shelf LED — warm white or color-changing",
      "Hidden cable channels — no visible wiring",
      "Cabinet doors sized for AV gear ventilation",
    ],
    materials_intro:
      "Every media wall is built to spec. Common material combinations we design around:",
    materials: [
      "White or veined marble-look panel + gold trim + black wood slats",
      "Travertine or PU stone + walnut slats + hidden warm LED",
      "Painted matte black + brushed brass + high-gloss accents",
      "Full walnut or oak grain + linear fireplace + open floating shelves",
    ],
    process: [
      "Consult on-site to measure and confirm layout",
      "Design proposal with materials + written scope",
      "Fabrication in our Tampa Bay shop",
      "1–3 day on-site installation with home protection",
    ],
    faqs: [
      {
        q: "Do you work with an existing TV or do I need a new one?",
        a: "Either — we size the recess or mount to your exact TV. If you're upgrading, we can spec the right size for the wall before you buy.",
      },
      {
        q: "Can you integrate an electric fireplace?",
        a: "Yes — recessed slim-line linear electric fireplaces are one of our most-requested features. We handle the electrical planning and finish surround.",
      },
      {
        q: "How long does a typical media wall take?",
        a: "Most single-wall projects install in 1–3 days on-site after 2–4 weeks of design and material lead time.",
      },
      {
        q: "How is a custom media wall priced?",
        a: "Cost depends on size, materials, fireplace, cabinetry and lighting — most single-wall projects range from $3,500 to $12,000+. Use the wall builder to get a working range.",
      },
    ],
  },

  "accent-walls": {
    slug: "accent-walls",
    title: "Accent & Feature Walls",
    seo_title: "Custom Accent Walls & Feature Walls Tampa Bay | Swift Luxury",
    meta_description:
      "Custom accent walls and feature walls in Tampa Bay — wood slats, marble-look panels, PU stone, painted paneling and backlit finishes designed and installed.",
    overline: "Accent walls",
    hero_heading: "Feature walls that finish the room",
    hero_sub:
      "Not every project needs a full media wall. Dining rooms, offices, bedrooms, foyers — a well-built accent wall is the difference between builder-grade and finished.",
    hero_image: "warm-travertine-slat",
    features: [
      "Wood-slat and acoustic panel installations",
      "Marble-look and travertine feature panels",
      "PU stone veneer texture walls",
      "Painted panel millwork with picture-frame moldings",
      "Full-height or partial-height installations",
      "Backlit ambient lighting integration",
      "Around fireplaces, headboards, or as room dividers",
    ],
    materials_intro:
      "Materials chosen for the light, the humidity and the way you'll live around the wall.",
    materials: [
      "Rift-cut oak or walnut slats over painted MDF backer",
      "Bookmatched marble-look panels with mitered edges",
      "PU stone veneer in warm, cool or neutral tones",
      "Shaker-panel millwork with matte enamel finish",
    ],
    process: [
      "Consult to review the wall + surrounding room",
      "Sample presentation on-site (light matters)",
      "Fabrication + delivery scheduling",
      "Single-day install for most accent walls",
    ],
    faqs: [
      {
        q: "Do accent walls need a TV or can they stand alone?",
        a: "They can absolutely stand alone. Many of our favorite projects are dining rooms, bedrooms and offices with no TV involved.",
      },
      {
        q: "Can I install a slat wall over painted drywall?",
        a: "In most cases yes, provided the wall is flat and structurally sound. We'll assess on the consult before quoting.",
      },
      {
        q: "How much does an accent wall cost?",
        a: "Accent walls typically range from $1,800 to $6,500 depending on size and material.",
      },
      {
        q: "Are these walls removable?",
        a: "Panel systems can be removed later; expect some drywall touch-up. We'll walk through what remediation would look like on the consult.",
      },
    ],
  },

  "slat-walls": {
    slug: "slat-walls",
    title: "Wood Slat & Acoustic Panels",
    seo_title: "Wood Slat & Acoustic Panel Wall Installation Tampa Bay | Swift Luxury",
    meta_description:
      "Wood slat wall and acoustic panel installation in Tampa Bay. Walnut, oak and painted slat features professionally installed by Swift Luxury Walls.",
    overline: "Slat & acoustic",
    hero_heading: "Wood slats and acoustic panels, done right",
    hero_sub:
      "Slat walls are simple in idea and unforgiving in execution — spacing, seams and vertical run all matter. We install them so they look intentional, not stuck on.",
    hero_image: "two-story-travertine",
    features: [
      "Full-height and partial slat installations",
      "Vertical, horizontal or angled runs",
      "Behind-TV slat features with hidden mounting",
      "Around fireplaces, columns and stairwells",
      "Acoustic-backed panels for sound-conscious rooms",
      "Custom stain-matching to floors or trim",
      "Sealed edges + mitered inside corners",
    ],
    materials_intro:
      "We work with acoustic-backed panel systems and solid-wood slats. Both have a place.",
    materials: [
      "Prefabricated acoustic panels (walnut, oak, black, natural)",
      "Solid-wood slats over MDF backer, custom-stained",
      "Painted MDF slats for a matte modern look",
      "PU-wrapped slats for a durable, uniform finish",
    ],
    process: [
      "Measure and photograph the wall",
      "Select panel type + finish direction",
      "Prep and level the substrate",
      "Install with hidden fasteners + mitered corners",
    ],
    faqs: [
      {
        q: "Are prefabricated slat panels as good as custom-built?",
        a: "Prefab acoustic panels are excellent for most applications — consistent, fast to install, and factory-finished. Custom-built solid wood is worth it when matching an existing floor or for very tall runs.",
      },
      {
        q: "Can slats be installed around outlets and switches?",
        a: "Yes. We plan the layout to keep outlets clean and cut the panel around them. Extenders may be needed if the panel is thick.",
      },
      {
        q: "Are slat walls loud when acoustically-backed?",
        a: "The acoustic backing dampens echo — helpful in tall great rooms and open plans. Ask us on the consult and we'll show you the difference.",
      },
      {
        q: "Can you match an existing wood tone?",
        a: "Yes — bring a photo of the floor or trim and we'll match it on the sample day.",
      },
    ],
  },

  fireplaces: {
    slug: "fireplaces",
    title: "Electric Fireplace Media Walls",
    seo_title: "Electric Fireplace Media Walls Tampa Bay | Swift Luxury",
    meta_description:
      "Electric fireplace media walls in Tampa Bay — linear recessed fireplaces built into custom media walls with TV mount, marble panels and hidden storage.",
    overline: "Fireplaces",
    hero_heading: "Electric fireplace media walls",
    hero_sub:
      "The layered look — TV floating above a linear fireplace, all built into one seamless wall. No masonry, no venting, no compromise on finish.",
    hero_image: "rgb-fireplace-media",
    features: [
      "Slim-line linear electric fireplaces recessed into the wall",
      "Widths from 36\" to 72\" with flame-height and color control",
      "Warm heat output on/off with the flames",
      "TV mounted safely above the unit with proper clearances",
      "Fireplace surround in marble, PU stone, painted or wood finish",
      "Cable pass-throughs for smart plugs, sensors and control units",
    ],
    materials_intro:
      "Every fireplace build is a wall build. We plan finish, mounting and electrical together.",
    materials: [
      "Recessed linear fireplace + full marble-look panel surround",
      "Recessed fireplace + PU stone veneer + walnut slat flanking",
      "Recessed fireplace + painted plaster look + floating console",
      "Recessed fireplace + travertine + wood-slat wings",
    ],
    process: [
      "Confirm fireplace model + heat spec + electrical",
      "Design surround + confirm TV clearance",
      "Frame the recess + run new electrical",
      "Install fireplace, finish the wall around it, punchlist",
    ],
    faqs: [
      {
        q: "Do I need a gas line or venting?",
        a: "No — electric fireplaces plug into a standard 120V outlet (or a 240V outlet for the high-heat models). No gas, no venting, no chimney work.",
      },
      {
        q: "Is it safe to mount a TV above an electric fireplace?",
        a: "Yes — most modern electric fireplaces have a forward-facing heat vent below the flames specifically so a TV can be safely mounted above. We check clearances on every build.",
      },
      {
        q: "Do the flames actually heat the room?",
        a: "The heat function is separate from the flame function on modern units — you can run the flames year-round with no heat, and turn heat on for cool evenings.",
      },
      {
        q: "What fireplace brands do you install?",
        a: "We install any homeowner-supplied unit, and we regularly recommend units from Modern Flames, Touchstone and Amantii for the balance of quality and value.",
      },
    ],
  },

  "built-ins": {
    slug: "built-ins",
    title: "Custom Consoles, Shelving & Built-Ins",
    seo_title: "Custom Consoles, Shelving & Built-Ins Tampa Bay | Swift Luxury",
    meta_description:
      "Floating consoles, bookcases, towers and custom built-ins for media walls and living rooms across Tampa Bay. Designed and installed by Swift Luxury Walls.",
    overline: "Built-ins & storage",
    hero_heading: "Cabinetry that disappears into the wall",
    hero_sub:
      "The best built-ins don't look like furniture — they look like part of the house. Floating consoles, towers, bookcases and cubbies designed to match your wall build.",
    hero_image: "warm-travertine-slat",
    features: [
      "Floating consoles up to 96\" wide",
      "Floor-standing cabinetry with kick-toe detail",
      "Left/right tower configurations",
      "Bookcase cubby presets — 3/3, 4/4, 5/5 or custom",
      "Push-latch or gold/matte hardware",
      "Ventilated doors for AV receivers and gaming consoles",
      "Painted, stained wood, high-gloss or PU-wrapped fronts",
    ],
    materials_intro:
      "We build with contractor-grade cabinet boxes and custom-fabricated fronts.",
    materials: [
      "Melamine or ply carcasses with edge-banded fronts",
      "Solid-wood door fronts, stained or painted",
      "High-gloss lacquered slab fronts (modern minimal)",
      "PU-wrapped slab fronts (uniform, durable, easy to clean)",
    ],
    process: [
      "Discuss what's stored (gear, glassware, kids, books)",
      "Design cubbies + doors + hardware selections",
      "Fabricate boxes + fronts to spec",
      "Install with anchors sized to the substrate",
    ],
    faqs: [
      {
        q: "Can you build around my receiver, sub or gaming console?",
        a: "Yes. We size cubbies specifically for the gear you own — with proper airflow and cable routing so it works long-term.",
      },
      {
        q: "Do you build free-standing pieces or only built-ins?",
        a: "Both — but the goal on most projects is to blend the cabinetry into the wall build so it reads as one continuous piece.",
      },
      {
        q: "How do floating consoles handle heavy gear?",
        a: "We anchor to studs (or masonry with sleeve anchors) and rate every floating console for its intended load. It'll hold what you need.",
      },
      {
        q: "Can I match cabinetry to my kitchen or existing millwork?",
        a: "In many cases yes. Bring photos or a door front on the consult and we'll match paint, stain and profile as closely as possible.",
      },
    ],
  },
};

export const SERVICE_LIST = [
  "media-walls",
  "accent-walls",
  "slat-walls",
  "fireplaces",
  "built-ins",
];
